import React, { Fragment, useCallback, useMemo } from 'react'
import { Modal, Row, Col, Button } from 'react-bootstrap'
import { banks, orderLabels } from '../../../constants/constants'
import OrderItemsDataTable from '../../Order/OrderItemsDataTable'
import { ORDER_ITEM_TYPE } from '../../../constants/item'
import { UNASSIGNED_TIME } from '../constants'
import { GridContainer, TextRight } from '../styles'
import { getTextColorByStatus } from '../utils'
import { useSelector } from 'react-redux'
import { cancelPayment, deliveryLottery } from '../utils/services'
import { formatPrice } from '../../../helpers/utils'
import ProductItemsDataTable from '../../Order/ProductItemsDataTable'
import styled from 'styled-components'

const CANCEL_PERMISSIONS = 'cancelPaymentSuccess'
const PAYMENT_SUCCESS = 'SUCCESS'

const OrderDetailModal = ({
  show,
  onHide,
  onCloseOrderModal,
  order,
  onCreateDelivery,
  paymentStatus,
  onCancelPayment,
}) => {
  const permissions = useSelector((state) => state.admin.permissions)
  const isCouponOrderType = 'couponOrderNo' in order

  const isDisplayCancelOrderButton = useMemo(() => {
    if (paymentStatus === null) return

    const isSeeCancelButton = permissions.includes(CANCEL_PERMISSIONS)

    return isSeeCancelButton && paymentStatus === PAYMENT_SUCCESS
  }, [paymentStatus, permissions])

  const handleClickCancelPayment = useCallback(
    async (id) => {
      try {
        await cancelPayment(id, true).then((isConfirmed) => {
          if (isConfirmed) {
            onCancelPayment?.()
            onHide()
          }
        })
      } catch (error) {
        console.error(error)
      }
    },
    [onCancelPayment, onHide],
  )

  const handleClickDelivery = useCallback(
    async (orderNo) => {
      try {
        await deliveryLottery(orderNo).then((payment) => {
          onCreateDelivery(payment)
          onHide()
        })
      } catch (error) {
        console.error(error)
      }
    },
    [onHide, onCreateDelivery],
  )

  return !order ? null : (
    <Modal
      show={show}
      size="lg"
      onHide={onCloseOrderModal}
      scrollable
      centered
      enforceFocus={false}
      style={{ padding: '2rem 0', background: 'rgba(0,0,0,0.3)' }}
    >
      <Modal.Header closeButton>คำสั่งซื้อ: #{order.couponOrderNo || order.orderNo}</Modal.Header>
      <Modal.Body>
        <GridContainer>
          <TextRight>ชื่อ :</TextRight>
          <span>
            {order.user.firstName} {order.user.lastName}
          </span>
        </GridContainer>
        <GridContainer>
          <TextRight>หมายเลขสมาชิก :</TextRight>
          <span>{order.user.userNo}</span>
        </GridContainer>
        <GridContainer>
          <TextRight>วันที่สร้างคำสั่งซื้อ :</TextRight>
          <span>{new Date(order.createdAt).toLocaleString('TH-th')}</span>
        </GridContainer>
        {order.approvedAt !== UNASSIGNED_TIME && (
          <GridContainer>
            <TextRight>วันที่คำสั่งซื้อถูกอนุมัติ :</TextRight>
            <span>{order.approvedAt ? new Date(order.approvedAt).toLocaleString('TH-th') : '-'}</span>
          </GridContainer>
        )}
        {!isCouponOrderType && (
          <GridContainer>
            <TextRight>เบอร์โทรศัพท์ :</TextRight>
            <span>{order.user.phoneNo}</span>
          </GridContainer>
        )}
        {!isCouponOrderType && (
          <GridContainer>
            <TextRight>บัญชีลูกค้า :</TextRight>
            <span>
              {order.user.bankName && (
                <span>
                  {banks[order.user.bankName]} {order.user.bankNo}
                </span>
              )}
            </span>
          </GridContainer>
        )}
        <GridContainer>
          <TextRight>สถานะ :</TextRight>
          <span style={{ color: getTextColorByStatus(order.status) }}>{orderLabels[order.status.toLowerCase()]}</span>
        </GridContainer>
        <GridContainer>
          <TextRight>ราคารวม :</TextRight>
          <span>
            <span style={{ color: 'red', fontSize: '1.125rem', fontWeight: '600' }}>
              {Number(order.totalPrice).toLocaleString('th-TH', { maximumFractionDigits: 2 })}
            </span>{' '}
            <span>บาท</span>
          </span>
        </GridContainer>
        <GridContainer>
          <TextRight>{isCouponOrderType ? 'จำนวนคูปอง' : 'จำนวนลอตเตอรี่'} :</TextRight>
          <span>
            <span style={{ color: 'black', fontSize: '1.125rem', fontWeight: '600' }}>
              {Number(order.totalItem).toLocaleString()}
            </span>
            <span> ใบ</span>
          </span>
        </GridContainer>
        {order.point && (
          <GridContainer>
            <TextRight>จำนวนนกพอยต์ที่ได้รับ :</TextRight>
            <span>
              <span style={{ color: 'black', fontSize: '1.125rem', fontWeight: '600' }}>
                {formatPrice(order?.point?.amount) || 0}
              </span>
              <span> พอยต์</span>
            </span>
          </GridContainer>
        )}
        <Row>
          <Col>
            <div className="border-bottom mt-4">
              <h3>
                รายละเอียดการสั่งซื้อ{' '}
                {order?.items?.[0]?.type === ORDER_ITEM_TYPE.LOTTERY ? (
                  <Fragment>
                    <span style={{ fontSize: '20px' }} className="text-info">
                      (ลอตเตอรี่)
                    </span>
                    <div className="flex py-2 gap-2">
                      {isDisplayCancelOrderButton && (
                        <Button variant="danger" size="sm" onClick={() => handleClickCancelPayment(order.paymentId)}>
                          ยกเลิกออเดอร์
                        </Button>
                      )}
                      <Button
                        variant="primary"
                        size="sm"
                        className="mx-2"
                        onClick={() => handleClickDelivery(order.orderNo)}
                      >
                        จัดส่ง
                      </Button>
                    </div>
                  </Fragment>
                ) : (
                  <span style={{ fontSize: '20px' }} className="text-danger">
                    {isCouponOrderType ? '(คูปอง)' : '(กล่องสุ่ม)'}
                    {isDisplayCancelOrderButton && !isCouponOrderType && (
                      <Button variant="danger" size="sm" onClick={() => handleClickCancelPayment(order.paymentId)}>
                        ยกเลิกออเดอร์
                      </Button>
                    )}
                  </span>
                )}
              </h3>
            </div>
          </Col>
        </Row>
        <Row>
          {isCouponOrderType ? (
            <ProductItemsDataTable order={order} />
          ) : (
            <OrderItemsDataTable order={order} orderType={order.items[0].type} />
          )}
        </Row>
        {!isCouponOrderType && order?.items?.length > 0 && (
          <Row>
            <Col className="overflow-auto w-space-no">
              {order?.items?.map((item) =>
                item?.info?.map((info) => {
                  return <ImageLotteries src={`${info.image.slice(0, -4)}_M.jpg`} alt="" />
                }),
              )}
            </Col>
          </Row>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default OrderDetailModal

const ImageLotteries = styled.img`
  width: 50%;
`
